import React, {
    createContext,
    useContext,
    useRef,
    useCallback,
    useState,
    useMemo,
    ReactNode,
    MutableRefObject,
} from 'react';

interface ModalData {
    id: string;
    [key: string]: any; // Allow for additional properties
}

interface ModalsContextValue {
    modals: ModalData[];
    container: HTMLElement | null;
    setContainer: (container: HTMLElement | null) => void;
    register: (id: string, data?: any) => void;
    unregister: (id: string) => void;
}

export const ModalsContext = createContext<ModalsContextValue>({
    modals: [],
    container: null,
    setContainer: () => {},
    register: () => {},
    unregister: () => {},
});

export const useModalsContext = (): ModalsContextValue => useContext(ModalsContext);

export const useModals = (): ModalData[] | undefined => {
    const { modals } = useContext(ModalsContext);
    return modals;
};

interface ModalsProviderProps {
    children: ReactNode;
    container?: HTMLElement | null;
}

export const ModalsProvider: React.FC<ModalsProviderProps> = ({
    children,
    container: initialContainer,
}) => {
    const [container, setContainer] = useState<HTMLElement | null>(initialContainer || null);
    const [modals, setModals] = useState<ModalData[]>([]);
    const modalsRef: MutableRefObject<ModalData[]> = useRef<ModalData[]>(modals);

    const register = useCallback(
        (id: string, data: any = null) => {
            const { current: currentModals } = modalsRef;
            const newModals: ModalData[] = [
                ...currentModals,
                {
                    id,
                    ...data,
                },
            ];
            setModals(newModals);
            modalsRef.current = newModals;
        },
        [], // Removed modals from dependency array
    );

    const unregister = useCallback(
        (id: string) => {
            const { current: currentModals } = modalsRef;
            const foundIndex = currentModals.findIndex(({ id: modalId }) => modalId === id);
            if (foundIndex !== -1) {
                const newModals: ModalData[] = currentModals.filter(
                    ({ id: modalId }) => modalId !== id,
                );
                setModals(newModals);
                modalsRef.current = newModals;
            }
        },
        [], // Removed modals from dependency array
    );

    const value: ModalsContextValue = useMemo(
        () => ({
            modals,
            container,
            setContainer,
            register,
            unregister,
        }),
        [modals, container, register, unregister], // Removed setContainer from dependency array
    );

    return <ModalsContext.Provider value={value}>{children}</ModalsContext.Provider>;
};
